import { useState, useContext } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import ContactUsApi from "../services/ContactUsApi";
import { UserContext } from "../context/UserContext";
import PageContainer from "../components/page-container/PageContainer";
import { FormTextField, FormSubmitButton } from "../components/form";
import AppSnackbar from "../components/snackbar/AppSnackbar";
import { useFormValidation, validators } from "../hooks/useFormValidation";
import { getErrorMessage } from "../utils/errorHandler";
import { FORM_CONTAINER_SX } from "../theme/formStyles";

const ContactUsPage = () => {
  const { t } = useTranslation();
  const contactUsApi = new ContactUsApi();
  const { user } = useContext(UserContext);

  const [name, setName] = useState(user?.displayName || "");
  const [email, setEmail] = useState(user?.email || "");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const {
    handleBlur,
    clearFieldError,
    validateForm,
    getFieldError,
    resetValidation,
  } = useFormValidation();

  // Validation rules
  const validationRules = {
    name: [validators.required],
    email: [validators.required, validators.email],
    subject: [validators.required],
    message: [validators.required, validators.minLength(10)],
  };

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  const clearForm = () => {
    setName(user?.displayName || "");
    setEmail(user?.email || "");
    setSubject("");
    setMessage("");
    resetValidation();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    const formData = { name, email, subject, message };
    if (!validateForm(formData, validationRules)) {
      return;
    }

    setLoading(true);
    try {
      await contactUsApi.createContactUs({
        name: name.trim(),
        email: email.trim(),
        subject: subject.trim(),
        message: message.trim(),
        uid: user?.uid || null,
      });
      clearForm();
      setSnackbar({
        open: true,
        message: t("contact.success_message"),
        severity: "success",
      });
    } catch (err) {
      console.error("ContactUsPage error:", err);
      setSnackbar({
        open: true,
        message: getErrorMessage(err, t),
        severity: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer>
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{ ...FORM_CONTAINER_SX, mt: 0 }}
      >
        <Typography variant="h3" align="center" gutterBottom>
          {t("contact.title")}
        </Typography>

        <FormTextField
          name="name"
          label={t("contact.name")}
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            clearFieldError("name");
          }}
          onBlur={() => handleBlur("name", name, validationRules.name)}
          error={getFieldError("name", t)}
          required
        />

        <FormTextField
          name="email"
          label={t("contact.email")}
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            clearFieldError("email");
          }}
          onBlur={() => handleBlur("email", email, validationRules.email)}
          error={getFieldError("email", t)}
          required
        />

        <FormTextField
          name="subject"
          label={t("contact.subject")}
          value={subject}
          onChange={(e) => {
            setSubject(e.target.value);
            clearFieldError("subject");
          }}
          onBlur={() => handleBlur("subject", subject, validationRules.subject)}
          error={getFieldError("subject", t)}
          required
        />

        <FormTextField
          name="message"
          label={t("contact.message")}
          value={message}
          onChange={(e) => {
            setMessage(e.target.value);
            clearFieldError("message");
          }}
          onBlur={() => handleBlur("message", message, validationRules.message)}
          error={getFieldError("message", t)}
          multiline
          rows={4}
          required
        />

        <FormSubmitButton
          label={t("contact.send")}
          loading={loading}
        />
      </Box>

      <AppSnackbar
        open={snackbar.open}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
        severity={snackbar.severity}
      />
    </PageContainer>
  );
};

export default ContactUsPage;
